/**
 * RegionPreferenceCookie
 *
 * Remembers the visitor's region in a per-stage cookie on `.app.justwalkout.com`, so that stage's
 * bare-domain redirect (a CloudFront Function in JWOStoreManagementPlatformCDK; its routing data is in
 * `lib/common/apexRedirectTargets.ts`) can send a returning visitor straight to that region instead
 * of the nearest one. Only a parent-domain cookie is visible to the bare domain.
 *
 * Prod and gamma share the parent domain, so each has its own cookie (FR3.5):
 *  - prod (`*.prod.app.justwalkout.com`, redirect from app.justwalkout.com): `smp_region`, one of
 *    `iad`, `dub`, `sin`. PDX stores map to `iad`: North America has one sign-in region, and PDX
 *    stores are reached in-app after store selection.
 *  - gamma (`*.gamma.app.justwalkout.com`, redirect from gamma.app.justwalkout.com):
 *    `smp_region_gamma`, one of `pdx`, `dub`, `sin`. Gamma has no IAD cell, so IAD maps to `pdx`.
 *
 * The cookie is only written on those regional app hosts; other hosts never see a bare-domain
 * redirect, and a cookie there could not be scoped to `.app.justwalkout.com` anyway.
 */

import { parseAppHost } from '@utils/AppHostUtil'

/** Prod cookie; must match REGION_COOKIE_NAME in the CDK package's apexRedirectTargets.ts. */
export const REGION_COOKIE_NAME = 'smp_region'
/** Gamma cookie; must match GAMMA_REGION_COOKIE_NAME in the CDK package's apexRedirectTargets.ts. */
export const GAMMA_REGION_COOKIE_NAME = 'smp_region_gamma'
export const REGION_COOKIE_DOMAIN = '.app.justwalkout.com'
/**
 * Deliberately 24h with a sliding refresh (every rememberRegion() call rewrites the expiry), to
 * match the native apps' own region cache: iOS RegionModule caches the resolved region (GPS or a
 * manual pick) in UserDefaults for 24h and refreshes it on every resolution.
 */
export const REGION_COOKIE_MAX_AGE_SECONDS = 24 * 60 * 60

export type RememberedRegion = 'iad' | 'pdx' | 'dub' | 'sin'

/** Stages with a bare-domain redirect, and so a remembered-region cookie. */
export type RememberedRegionStage = 'prod' | 'gamma'

interface IStageCookie {
    readonly cookieName: string
    /** Airport codes and AWS regions the app knows, mapped to a region the stage redirects to. */
    readonly regions: Readonly<Record<string, RememberedRegion>>
}

/**
 * This is not a copy of @utils/RegionRealms's airport<->realm tables: RegionRealms is a 1:1
 * conversion between the two vocabularies, while this map also *consolidates* targets a stage
 * does not serve onto the one it does -- prod has no PDX bare-domain target, so a PDX store (or
 * the `pdx`/`us-west-2` spellings of it) collapses to `iad` here; gamma, which has no IAD cell,
 * does the reverse. A test below pins the airport-code keys of both prod and gamma to
 * RegionRealms.AIRPORT_CODES, so a cell added there cannot be silently missing from this table.
 */
const STAGE_COOKIES: Readonly<Record<RememberedRegionStage, IStageCookie>> = {
    prod: {
        cookieName: REGION_COOKIE_NAME,
        regions: {
            'iad': 'iad',
            'pdx': 'iad',
            'dub': 'dub',
            'sin': 'sin',
            'us-east-1': 'iad',
            'us-west-2': 'iad',
            'eu-west-1': 'dub',
            'ap-southeast-1': 'sin',
        },
    },
    gamma: {
        cookieName: GAMMA_REGION_COOKIE_NAME,
        regions: {
            'iad': 'pdx',
            'pdx': 'pdx',
            'dub': 'dub',
            'sin': 'sin',
            'us-east-1': 'pdx',
            'us-west-2': 'pdx',
            'eu-west-1': 'dub',
            'ap-southeast-1': 'sin',
        },
    },
}

/** The remembered-region cookie name for a stage. */
export function regionCookieName (stage: RememberedRegionStage): string {
    return STAGE_COOKIES[stage].cookieName
}

/**
 * Maps an airport code (any case, e.g. `PDX`) or AWS region (e.g. `eu-west-1`) to the region the
 * stage's bare domain redirects to, or null when it is not a known region.
 */
export function toRememberedRegion (
    airportOrRealm: string | null | undefined,
    stage: RememberedRegionStage = 'prod',
): RememberedRegion | null {
    if (typeof airportOrRealm !== 'string') return null
    const regions = STAGE_COOKIES[stage].regions
    const key = airportOrRealm.trim().toLowerCase()
    return Object.prototype.hasOwnProperty.call(regions, key) ? regions[key] : null
}

/**
 * The region remembered in the stage's cookie, or null when there is none, its value is not one of
 * the regions the stage redirects to (a stale or tampered cookie is ignored, as the redirect does),
 * or duplicate entries for the cookie name disagree.
 *
 * Mirrors regionFromCookie in the CloudFront Function (JWOStoreManagementPlatformCDK
 * lib/cloudfront-functions/apex-redirect.js): a client (or an intermediary) can send more than one
 * `Cookie` header value for the same name, and the function treats disagreeing duplicates as no
 * cookie at all rather than picking one. If this reader instead picked the first entry, it could
 * disagree with the function -- e.g. `smp_region=dub; smp_region=sin` would make the function
 * decide no region is remembered (so it would send the viewer to the manual picker) while this
 * reader thought `dub` was remembered and skipped offering the picker on the login page.
 */
export function readRememberedRegion (
    stage: RememberedRegionStage,
    cookies: string = document.cookie,
): RememberedRegion | null {
    const prefix = `${regionCookieName(stage)}=`
    const values = cookies
        .split(';')
        .map((c) => c.trim())
        .filter((c) => c.startsWith(prefix))
        .map((c) => c.slice(prefix.length))
    if (values.length === 0) return null
    if (values.some((v) => v !== values[0])) return null
    // Only the stage's own targets are valid values; the airport→target mapping is not applied here.
    const region = toRememberedRegion(values[0], stage)
    return region === values[0] ? region : null
}

/** The stage of a regional app host that has a bare-domain redirect, else null. */
function rememberedRegionStage (hostname: string): RememberedRegionStage | null {
    const stage = parseAppHost(hostname)?.stage
    return stage === 'prod' || stage === 'gamma' ? stage : null
}

/**
 * Writes the stage's remembered-region cookie for 24 hours.
 *
 * @param airportOrRealm the store's or chosen region, as an airport code or AWS region
 * @param hostname the current host; the cookie is only written on `*.prod.app.justwalkout.com`
 *                 (`smp_region`) and `*.gamma.app.justwalkout.com` (`smp_region_gamma`)
 * @returns true when the cookie was written
 */
export function rememberRegion (
    airportOrRealm: string | null | undefined,
    hostname: string = window.location.hostname,
): boolean {
    const stage = rememberedRegionStage(hostname)
    if (!stage) return false

    const region = toRememberedRegion(airportOrRealm, stage)
    if (!region) {
        console.warn('RegionPreferenceCookie: region not remembered, not a known region')
        return false
    }

    try {
        document.cookie = `${regionCookieName(stage)}=${region}; Domain=${REGION_COOKIE_DOMAIN}; Path=/; ` +
            `Max-Age=${REGION_COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`
        return true
    } catch (err) {
        // Cookies can be blocked by the browser; the redirect then falls back to the nearest region.
        console.error('RegionPreferenceCookie: could not store the remembered region', (err as Error)?.name)
        return false
    }
}
